// API endpoints
const MOBILE_API_PREFIX = ''
const CATALOGS_API_PREFIX = ''

export const endpoints = {
  auth: {
    // Phone auth
    phoneSignin: `${MOBILE_API_PREFIX}/auth/phone/sign-in`,
    phoneSendCode: `${MOBILE_API_PREFIX}/auth/phone/send-code`,
    phoneVerifyCode: `${MOBILE_API_PREFIX}/auth/phone/verify-code`,

    // Email auth
    emailSignin: `${MOBILE_API_PREFIX}/auth/email/sign-in`,
    emailVerifyCode: `${MOBILE_API_PREFIX}/auth/email/verify-code`,

    // Social auth
    googleSignin: `${MOBILE_API_PREFIX}/auth/google/sign-in`,
    appleSignin: `${MOBILE_API_PREFIX}/auth/apple/sign-in`,
    telegramSignin: `${MOBILE_API_PREFIX}/auth/telegram/sign-in`,
    telegramInitiate: `${MOBILE_API_PREFIX}/auth/telegram/initiate`,
    telegramLinkAccount: `${MOBILE_API_PREFIX}/auth/telegram/link/account`,
    telegramLinkOauth: `${MOBILE_API_PREFIX}/auth/telegram/link/oauth`,

    // Password
    verifyPassword: `${MOBILE_API_PREFIX}/auth/verify-password`,
    setupPassword: `${MOBILE_API_PREFIX}/auth/setup-password`,
    recoverPassword: `${MOBILE_API_PREFIX}/auth/recover-password`,

    sendRecoveryCodeToEmail: `${MOBILE_API_PREFIX}/auth/email/recovery/send-code`,
    checkRecoveryCodeSentToEmail: `${MOBILE_API_PREFIX}/auth/email/recovery/verify-code`,
    sendRecoveryCodeToPhone: `${MOBILE_API_PREFIX}/auth/phone/recovery/send-code`,
    checkRecoveryCodeSentToPhone: `${MOBILE_API_PREFIX}/auth/phone/recovery/verify-code`,

    // Session
    refreshTokens: `${MOBILE_API_PREFIX}/auth/refresh-tokens`,
    logout: `${MOBILE_API_PREFIX}/auth/logout`,
    available: `${MOBILE_API_PREFIX}/auth/available`,
  },

  identity: {
    me: `${MOBILE_API_PREFIX}/identity/me`,
    getProfile: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/${userId}/get_profile`,
    setupProfile: `${MOBILE_API_PREFIX}/identity/setup-profile`,
    updateProfile: `${MOBILE_API_PREFIX}/identity/update-profile`,
    updatePassword: `${MOBILE_API_PREFIX}/identity/reset_password`,
    uploadAvatar: `${MOBILE_API_PREFIX}/identity/upload-avatar`,
    // Phone management for existing accounts
    sendPhoneVerificationCode: `${MOBILE_API_PREFIX}/identity/send-phone-verification-code`,
    verifyPhoneUpdate: `${MOBILE_API_PREFIX}/identity/verify-phone`,
    // Email verification
    resendVerificationEmail: `${MOBILE_API_PREFIX}/identity/resend-verification-email`,
    // KYC verification
    kyc: `${MOBILE_API_PREFIX}/identity/kyc`,
    kycSDKAccessToken: `${MOBILE_API_PREFIX}/identity/kyc/sdk/access-token`,
    kycInfo: `${MOBILE_API_PREFIX}/identity/kyc/info`,
    updateKycInfo: `${MOBILE_API_PREFIX}/identity/kyc/update`,
    // Card verification
    verifyCardIssueStatus: `${MOBILE_API_PREFIX}/identity/kyc/card/issue-state`,
    // Contacts and users
    createKemCard: `${MOBILE_API_PREFIX}/identity/kyc/card`,
    fetchUsersByPhoneList: `${MOBILE_API_PREFIX}/identity/get_users_by_phone_list`,
    fetchNonUsersByPhoneList: `${MOBILE_API_PREFIX}/identity/get_non_users_by_phone_list`,
    search: `${MOBILE_API_PREFIX}/identity/users/search`,
    favoriteUsers: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/favorites`,
    addUserToFavorites: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/favorites`,
    unfavoriteUser: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/favorites`,
    // Block/unblock users
    block: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/block`,
    unblock: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/unblock`,
    blockedUsersList: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/block-list`,
    // Report user
    reportsUser: (userId: string) =>
      `${MOBILE_API_PREFIX}/identity/users/${userId}/reports`,
    // Request user data
    requestYourData: `${MOBILE_API_PREFIX}/identity/request-data`,
    // Dinari KYC
    dinariKycStatus: `${MOBILE_API_PREFIX}/identity/kyc/dinari/status`,
    dinariStartKyc: `${MOBILE_API_PREFIX}/identity/kyc/dinari`,
  },

  wallet: {
    getWallet: `${MOBILE_API_PREFIX}/wallet/get_wallet`,
    updateWallet: `${MOBILE_API_PREFIX}/wallet/update_wallet`,
    cryptoAddress: `${MOBILE_API_PREFIX}/wallet/crypto-address`,
    aboutCurrencyInBlockchain: (
      currency_isin: string,
      blockchain_code: string,
    ) =>
      `${CATALOGS_API_PREFIX}/currencies/${currency_isin}/${blockchain_code}`,
    // Withdraw crypto address endpoints
    withdrawCryptoAddressByNetwork: (networkCode: string) =>
      `${MOBILE_API_PREFIX}/wallet/crypto-address/withdraw/${networkCode}`,
    withdrawCryptoAddress: `${MOBILE_API_PREFIX}/wallet/crypto-address/withdraw`,
    // Kem Card endpoints
    createKemCard: `${MOBILE_API_PREFIX}/wallet/card/issue`,
    getKemCard: (cardId: string) =>
      `${MOBILE_API_PREFIX}/wallet/card/${cardId}`,
    /** @deprecated Use getKemCardEncrypted for secure card data retrieval */
    getKemCardLegacy: (cardId: string) =>
      `${MOBILE_API_PREFIX}/wallet/card/${cardId}`,
    /**
     * Encrypted card endpoint for RAIN provider
     * Requires session_id query parameter for decryption
     */
    getKemCardEncrypted: (cardId: string) =>
      `${MOBILE_API_PREFIX}/wallet/card/${cardId}/encrypted`,
    deleteKemCard: (cardId: string) =>
      `${MOBILE_API_PREFIX}/wallet/card/${cardId}`,
    blockKemCard: `${MOBILE_API_PREFIX}/wallet/card/block`,
    unblockKemCard: `${MOBILE_API_PREFIX}/wallet/card/unblock`,
    getInterlaceWidgetToken: (cardId: string) =>
      `${MOBILE_API_PREFIX}/wallet/card/${cardId}/access-token`,
    getCardPin: (cardId: string) => `${MOBILE_API_PREFIX}/wallet/card/${cardId}/pin`,
    setCardPin: `${MOBILE_API_PREFIX}/wallet/card/pin/set`,
    // Unigox off-ramp endpoints
    getUnigoxOffRampPaymentRails: `${MOBILE_API_PREFIX}/wallet/unigox/offramp/payment-rails`,
    getUnigoxOffRampInstitutions: `${MOBILE_API_PREFIX}/wallet/unigox/offramp/institutions`,
    unigoxOffRampPaymentDetails: `${MOBILE_API_PREFIX}/wallet/unigox/offramp/payment-details`,
    deleteUnigoxOffRampPaymentDetails: (paymentDetailsId: string) =>
      `${MOBILE_API_PREFIX}/wallet/unigox/offramp/payment-details/${paymentDetailsId}`,
  },

  settings: {
    features: `${MOBILE_API_PREFIX}/settings/features`,
  },

  transfer: {
    transactions: `${MOBILE_API_PREFIX}/transfer/transactions`,
    transactionById: (transactionId: string) =>
      `${MOBILE_API_PREFIX}/transfer/transactions/${transactionId}`,
    createInteractRequest: `${MOBILE_API_PREFIX}/transfer/create_interact_request`,
    fetchPaymentRequestsHistory: `${MOBILE_API_PREFIX}/transfer/payment-requests/history`,
    declineRequest: (requestId: string) =>
      `${MOBILE_API_PREFIX}/transfer/interact-requests/${requestId}/decline`,
    cancelRequest: (requestId: string) =>
      `${MOBILE_API_PREFIX}/transfer/interact-requests/${requestId}/cancel`,
    acceptRequest: (requestId: string) =>
      `${MOBILE_API_PREFIX}/transfer/interact-requests/${requestId}/accept`,
    takeBackRequest: (requestId: string) =>
      `${MOBILE_API_PREFIX}/transfer/interact-requests/${requestId}/take-back`,
    interactRequestsCount: `${MOBILE_API_PREFIX}/transfer/interact-requests/count`,
    getOnRampPricePerUnit: `${MOBILE_API_PREFIX}/transfer/on-ramp-price-per-unit`,
    // Internal transfers
    internal: `${MOBILE_API_PREFIX}/transfer/internal`,
    // On-ramp
    onRamp: `${MOBILE_API_PREFIX}/transfer/on-ramp`,
    onRampLink: `${MOBILE_API_PREFIX}/transfer/on-ramp/paylink/init`,
    priceLimitsAllCurrency: `${MOBILE_API_PREFIX}/transfer/on-ramp/price-limits`,
    priceQuotesAll: `${MOBILE_API_PREFIX}/transfer/on-ramp/price-quotes-all`,
    cryptoPriceQuotes: `${MOBILE_API_PREFIX}/transfer/on-ramp/crypto-price-quotes`,
    onRampPricePerUnit: (cryptoCode: string) =>
      `${MOBILE_API_PREFIX}/transfer/price-per-unit/${cryptoCode}`,
    // Conversion endpoints
    estimateConvertOrSellFee: `${MOBILE_API_PREFIX}/transfer/estimate-convert-or-sell-fee`,
    convertOrSell: `${MOBILE_API_PREFIX}/transfer/convert-or-sell`,
    // External withdraw endpoints
    externalWithdrawCalculateFee: `${MOBILE_API_PREFIX}/transfer/withdraw/external/calc-fees`,
    externalWithdraw: `${MOBILE_API_PREFIX}/transfer/withdraw/external`,
    checkExternalWalletAddress: `${MOBILE_API_PREFIX}/transfer/withdraw/external/check-address`,

    // SAVINGS
    goals: `${MOBILE_API_PREFIX}/transfer/savings/goals`,
    goalById: (goalId: string) =>
      `${MOBILE_API_PREFIX}/transfer/savings/goals/${goalId}`,
    goalTransferIn: `${MOBILE_API_PREFIX}/transfer/savings/goals/transfer-in`,
    goalTransferOut: `${MOBILE_API_PREFIX}/transfer/savings/goals/transfer-out`,
    estimateEarnings: `${MOBILE_API_PREFIX}/transfer/savings/estimate/earnings`,
    estimateTransferOutFee: `${MOBILE_API_PREFIX}/transfer/savings/estimate/transfer-out-fee`,
    savingGoalHistory: (goalId: string) =>
      `${MOBILE_API_PREFIX}/transfer/savings/goals/${goalId}/history`,
    savingGoalTransferDetails: (goalId: string, transferId: string) =>
      `${MOBILE_API_PREFIX}/transfer/savings/goals/${goalId}/${transferId}`,

    // CASHBACK
    cashbackInfo: `${MOBILE_API_PREFIX}/transfer/cashback/info`,

    // OFF-RAMP
    offRampPriceLimits: `${MOBILE_API_PREFIX}/transfer/off-ramp/price-limits`,
    offRampPriceEstimate: `${MOBILE_API_PREFIX}/transfer/off-ramp/estimate`,
    offRampPriceQuotes: `${MOBILE_API_PREFIX}/transfer/off-ramp/quote`,
    initiateOffRamp: `${MOBILE_API_PREFIX}/transfer/off-ramp`,
    confirmOffRampPayment: `${MOBILE_API_PREFIX}/transfer/off-ramp/confirm-payment`,
    cancelOffRamp: `${MOBILE_API_PREFIX}/transfer/off-ramp/cancel`,
  },

  paymentLink: {
    info: (code: string) =>
      `${MOBILE_API_PREFIX}/payment-link/${code}/info`,
    claim: `${MOBILE_API_PREFIX}/payment-link/claim`,
  },

  notifier: {
    remindRequest: (requestId: string) =>
      `${MOBILE_API_PREFIX}/notifier/interact-requests/${requestId}/remind`,
  },

  blockchains: {
    getBlockchains: (currencyIsin: string) =>
      `${CATALOGS_API_PREFIX}/blockchains/${currencyIsin}`,
  }, //should we add this to catalogs as in the mobile version?
  catalogs: {
    messagingMethods: {
      getMessagingMethods: `${CATALOGS_API_PREFIX}/messaging-methods`,
    },
    countries: {
      getCountries: `${CATALOGS_API_PREFIX}/countries`,
    },
    currencies: {
      getCurrencies: `${CATALOGS_API_PREFIX}/currencies`,
      getCurrencyBlockchainInfos: (
        currency_isin: string,
        blockchain_code: string,
      ) =>
        `${CATALOGS_API_PREFIX}/currencies/${currency_isin}/${blockchain_code}`,
    },
    blockchains: {
      getBlockchains: (currency_isin: string) =>
        `${CATALOGS_API_PREFIX}/blockchains/${currency_isin}`,
    },
  },
  history: {
    cryptoHistoryPrice: (blockchain: string, period: string) =>
      `${MOBILE_API_PREFIX}/history/${blockchain}/${period}`,
  },
  mfa: {
    startMfa: `${MOBILE_API_PREFIX}/mfa/challenges`,
    verifyMfa: (challenge_id: string) =>
      `${MOBILE_API_PREFIX}/mfa/challenges/${challenge_id}/verify`,
  },
  boxo: {
    auth: `${MOBILE_API_PREFIX}/boxo/auth`,
  },
  shares: {
    fetchAllStocks: `${MOBILE_API_PREFIX}/shares/stocks`,
    fetchMyStocks: `${MOBILE_API_PREFIX}/shares/portfolio_stocks`,
    fetchStocksCategories: `${MOBILE_API_PREFIX}/shares/stock_categories`,
    getMarketInfo: `${MOBILE_API_PREFIX}/shares/market_info`,
    getStockDetails: `${MOBILE_API_PREFIX}/shares/stock_details`,
    getStockHistoricalPrices: `${MOBILE_API_PREFIX}/shares/stock_historical_prices`,
    getStocksCount: `${MOBILE_API_PREFIX}/shares/stocks_count`,
    getPricePerUnit: `${MOBILE_API_PREFIX}/shares/price_per_unit`,
    getShareQuotes: `${MOBILE_API_PREFIX}/shares/order_requests/fee_quote`,
    limitBuy: `${MOBILE_API_PREFIX}/shares/order_requests/limit_buy`,
    limitSell: `${MOBILE_API_PREFIX}/shares/order_requests/limit_sell`,
    getPortfolioInfo: `${MOBILE_API_PREFIX}/shares/portfolio_info`,
    getPortfolioPosition: `${MOBILE_API_PREFIX}/shares/portfolio_position`,
  },
}
